import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, PriorityBadge, EmptyState } from "@/components/common/Bits";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { ListChecks, ArrowRight, CheckCircle2, Play } from "lucide-react";
import { toast } from "sonner";

export default function Tasks() {
  const { user, can } = useAuth();
  const [rows, setRows] = useState(null);
  const [scope, setScope] = useState("mine");

  const load = () => api.get("/tasks", { params: { mine: scope === "mine" } }).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [scope]);

  const setStatus = async (id, status) => {
    try { await api.put(`/tasks/${id}`, { status }); toast.success("Tarea actualizada"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  if (!rows) return <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>;

  const open = rows.filter((t) => t.status !== "Completada");
  const done = rows.filter((t) => t.status === "Completada");

  return (
    <div>
      <PageHeader title="Mis tareas" subtitle={`Evaluaciones, controles, votos, reevaluaciones y evidencias pendientes de ${user.name}.`} testId="tasks-header">
        <Tabs value={scope} onValueChange={setScope}>
          <TabsList className="bg-muted">
            <TabsTrigger value="mine" data-testid="tasks-mine-tab">Mis tareas</TabsTrigger>
            <TabsTrigger value="all" data-testid="tasks-all-tab">Todas</TabsTrigger>
          </TabsList>
        </Tabs>
      </PageHeader>

      {open.length === 0 && <EmptyState icon={ListChecks} title="Sin tareas pendientes" subtitle="Excelente: no tiene trabajo pendiente en el ciclo de gobierno." />}
      <div className="space-y-3">
        {open.map((t) => (
          <Card key={t.id} className="shadow-card border-border" data-testid={`task-card-${t.id}`}>
            <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-secondary text-secondary-foreground">{t.type}</span>
                  <PriorityBadge priority={t.priority} />
                  <StatusBadge status={t.status} />
                </div>
                <p className="font-medium text-sm mt-2">{t.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{t.description}</p>
                <p className="text-[11px] text-muted-foreground mt-1">Responsable: {t.assignee} · Vence {fmtDate(t.due_date)}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {can.tasks && t.status === "Pendiente" && (
                  <Button size="sm" variant="outline" className="h-8 text-xs border-border" onClick={() => setStatus(t.id, "En curso")} data-testid={`task-start-${t.id}`}>
                    <Play className="h-3 w-3 mr-1" /> Iniciar
                  </Button>
                )}
                {can.tasks && (t.status === "En curso" || t.status === "Vencida") && (
                  <Button size="sm" variant="outline" className="h-8 text-xs border-success/40 text-success hover:bg-success/5" onClick={() => setStatus(t.id, "Completada")} data-testid={`task-complete-${t.id}`}>
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Completar
                  </Button>
                )}
                {t.link && (
                  <Button asChild size="sm" className="h-8 text-xs bg-primary hover:bg-primary/90 text-primary-foreground">
                    <Link to={t.link}>Ir al objeto <ArrowRight className="h-3 w-3 ml-1" /></Link>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {done.length > 0 && (
        <>
          <h2 className="font-display text-sm font-semibold text-muted-foreground uppercase tracking-wider mt-10 mb-3">Completadas</h2>
          <div className="space-y-2 opacity-70">
            {done.map((t) => (
              <div key={t.id} className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-2.5">
                <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
                <p className="text-sm truncate flex-1">{t.title}</p>
                <StatusBadge status={t.status} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
